import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggerService } from 'src/app/services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class CustomizeWorkspaceService {
  private workspaceCustomization = new BehaviorSubject<any>(this.loadData());
  
  workspaceCustomization$ = this.workspaceCustomization.asObservable();

  constructor(private logger: LoggerService) {
    this.refresh();
  }

  /**
   * Refreshes the internal state by re-loading data from localStorage.
   * Useful when the merchant_id has changed (e.g. after login).
   */
  refresh(): void {
    try {
      const data = this.loadData();
      this.workspaceCustomization.next(data);
    } catch (e) {
      this.logger.error('Error refreshing workspace customization:', e);
    }
  }

  private getStorageKey(): string {
    try {
      const merchantId = localStorage.getItem('merchant_id');
      return merchantId != null && merchantId !== '' ? `workspaceCustomization_${merchantId}` : 'workspaceCustomization';
    } catch (e) {
      this.logger.error('Error accessing localStorage in getStorageKey:', e);
      return 'workspaceCustomization';
    }
  }

  private hasFetchedData = false;

  getData(): any {
    return this.workspaceCustomization.getValue();
  }

  updateData(data: any): void {
    if (data == null) {
      this.logger.warn('Attempted to update workspace customization with null/undefined data');
      return;
    }
    this.hasFetchedData = true;
    const storageKey = this.getStorageKey();

    // Load existing data for merging
    let currentData = this.loadData();
    let currentModules: any[] = [];
    if (currentData) {
      currentModules = Array.isArray(currentData) ? currentData : (currentData?.data || []);
    }

    // Normalize incoming modules
    let incomingModules: any[] = [];
    if (Array.isArray(data)) {
      incomingModules = data;
    } else if (data?.data && Array.isArray(data.data)) {
      incomingModules = data.data;
    } else if (data?.slug) {
      incomingModules = [data]; // Single module update
    }

    // Upsert logic: Update if slug present, otherwise set (Add)
    // We use a Map to handle de-duplication by slug
    const modulesMap = new Map();
    if (Array.isArray(currentModules)) {
      currentModules.forEach(m => { if (m?.slug) modulesMap.set(m.slug, m); });
    }
    if (Array.isArray(incomingModules)) {
      incomingModules.forEach(m => {
        if (m?.slug) {
          const existing = modulesMap.get(m.slug) || {};
          modulesMap.set(m.slug, { ...existing, ...m });
        }
      });
    }

    const mergedModules = Array.from(modulesMap.values());
    
    // Maintain the top-level structure (either array or { data: [...] })
    const finalToStore = (currentData && !Array.isArray(currentData)) 
      ? { ...currentData, data: mergedModules } 
      : mergedModules;

    try {
      localStorage.setItem(storageKey, JSON.stringify(finalToStore));
    } catch (e) {
      this.logger.error('Error saving workspace customization to localStorage:', e);
    }
    this.workspaceCustomization.next(finalToStore);
  }

  setFetched(status: boolean): void {
    this.hasFetchedData = status;
  }

  isFetched(): boolean {
    return this.hasFetchedData;
  }

  getWorkspaceCustomizationObservable(): Observable<any> {
    return this.workspaceCustomization.asObservable();
  }

  clearWorkspaceCustomization(): void {
    const storageKey = this.getStorageKey();
    localStorage.removeItem(storageKey);
    this.workspaceCustomization.next(null);
  }

  private loadData(): any {
    try {
      const storageKey = this.getStorageKey();
      const storedData = localStorage.getItem(storageKey);
      
      if (storedData == null || storedData === '') {
        return null;
      }

      const parsedData = JSON.parse(storedData);
      return parsedData;
    } catch (error) {
      this.logger.error('Error parsing workspace customization data from localStorage:', error);
      // Clear corrupted data to prevent future errors
      const storageKey = this.getStorageKey();
      if (storageKey != null) {
        localStorage.removeItem(storageKey);
      }
      return null;
    }
  }
}

